import React, { useRef, useState } from 'react';
import {
  Button, Form, Modal, Table,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import MessagePresetsService from '../../../Services/MessagePresetsService';
import {
  flattenLines, hasLineBreak, oneLine, pasteOneLine,
} from '../../../Utils/oneLine';
import { templateBlocks, TEMPLATE_VARIABLE_MAX } from '../../../Utils/passengerTemplate';
import {
  EMAIL, isEmailChannel, messageChannel, WHATSAPP,
} from '../../../Utils/messageChannel';

/**
 * Add or edit one saved passenger message, on either channel.
 *
 * SHAPED LIKE `BookingServiceInputModal`: one modal for both the add and the
 * edit (`originalPreset.id` is what tells them apart), `Error` at the top,
 * `FormControlFeedback` under the fields the server validates, Cancel and
 * Save in the footer. The parent re-reads the list on success rather than
 * this patching it.
 *
 * THE CHANNEL IS THE FIRST FIELD, AND IT CHANGES THE FORM. A WhatsApp
 * snippet and an email one are not the same object wearing a different hat:
 *
 *   WhatsApp   goes into the `{{2}}` slot of the approved
 *              `we_have_a_message_for_you` template. One line, capped at
 *              `TEMPLATE_VARIABLE_MAX`, no greeting — the template writes
 *              that — and previewed as the three blocks the passenger reads.
 *   Email      has a SUBJECT of its own, no template, no line rule and no
 *              cap. Paragraphs are the point of it.
 *
 * So switching the channel swaps the body field's shape, shows or hides the
 * subject, and swaps the preview. A form that only relabelled itself would
 * invite somebody to write an email snippet into a one-line box and wonder
 * where their paragraphs went.
 *
 * SWITCHING AWAY FROM EMAIL CLEARS THE SUBJECT ON SAVE, not on the click.
 * The field stops being shown, but the words stay in state until Save, so a
 * switch made to look at something and switched straight back loses nothing.
 * What is STORED always matches the channel: `subject: ''` on a WhatsApp row,
 * never a stranded line nobody can see and nobody can send.
 *
 * WHAT IT REUSES RATHER THAN RESTATES:
 *
 *   `templateBlocks`     the three blocks the passenger actually reads on
 *                        WhatsApp, so the author SEES the greeting the
 *                        template supplies instead of being told about it.
 *                        Same module the two composers preview with, so the
 *                        preview here and the preview at send time cannot
 *                        disagree.
 *   `oneLine` / `flattenLines` / `pasteOneLine`
 *                        a WHATSAPP body is one line, because it becomes a
 *                        template variable and a line break fails the send.
 *                        A pasted paragraph is flattened to spaces, never
 *                        refused — nobody is punished for a paste. None of
 *                        it runs on the email body.
 *   `TEMPLATE_VARIABLE_MAX`
 *                        the cap belongs to the template, not to a screen —
 *                        and therefore not to the email channel either.
 *   `Utils/messageChannel`
 *                        the one place that says an absent channel means
 *                        WhatsApp, which is what the rows already in
 *                        production carry.
 *
 * THE VARIABLES ARE THE SERVER'S LIST, passed in from the parent's read. The
 * dashboard does not know the vocabulary and must not keep its own copy: a
 * help table that named a variable the API cannot fill would send the office
 * to write a message that is refused on save. They work the same on both
 * channels — the server fills them from the booking at the moment the
 * message is picked, whichever way it then travels.
 *
 * THE PREVIEW SHOWS THE EXAMPLE VALUES, not the raw `{pickup}`, so what is
 * on screen is the shape of the sentence a passenger will read. The real
 * values come off the real booking at send time.
 */

const PLACEHOLDER = 'Your message appears here';
const SUBJECT_PLACEHOLDER = 'Your subject appears here';

/** The channel picker's two options, in the order they are offered. */
const CHANNELS = [
  { value: WHATSAPP, label: 'WhatsApp', icon: 'fab fa-whatsapp text-success' },
  { value: EMAIL, label: 'Email', icon: 'fa fa-envelope text-primary' },
];

/** `{pickup}` → the example value, for the preview only. */
const withExamples = (body, variables) => variables.reduce(
  (text, variable) => text.split(variable.placeholder).join(variable.example),
  String(body || ''),
);

function TemplatePreview({ body, variables }) {
  const blocks = templateBlocks({
    // The template's `{{1}}` is derived from the booking by the server; the
    // example name here stands in for whoever it turns out to be.
    first: variables.find((v) => v.token === 'name')?.example || 'Anna',
    body: withExamples(body, variables),
    placeholder: PLACEHOLDER,
  });

  return (
    <div className="border rounded-3 bg-warning-subtle px-3 py-2" data-testid="preset-preview">
      {blocks.map((block) => (
        <p
          key={block.id}
          className={[
            'mb-0 py-1',
            block.fixed ? 'text-secondary fw-semibold' : 'fw-bold',
            block.empty ? 'fst-italic text-muted fw-normal' : '',
          ].filter(Boolean).join(' ')}
          data-testid={`preset-preview-${block.id}`}
        >
          {block.text}
        </p>
      ))}
    </div>
  );
}

/**
 * The email as the passenger will read it: the subject line, then the body
 * with its own paragraphs.
 *
 * NO GREETING AND NO FIXED LEAD, because there is no template to supply
 * them. Showing the WhatsApp preview here would promise two lines that are
 * never sent — the preview is only worth having while it is honest.
 */
function EmailPreview({ subject, body, variables }) {
  const line = withExamples(subject, variables).trim();
  const written = withExamples(body, variables).trim();

  return (
    <div className="border rounded-3 bg-warning-subtle px-3 py-2" data-testid="preset-preview">
      <div className="small text-secondary fw-semibold">Subject</div>
      <p
        className={`mb-2 ${line ? 'fw-bold' : 'fst-italic text-muted'}`}
        data-testid="preset-preview-subject"
      >
        {line || SUBJECT_PLACEHOLDER}
      </p>
      <div className="small text-secondary fw-semibold">Message</div>
      <p
        className={`mb-0 ${written ? 'fw-bold' : 'fst-italic text-muted'}`}
        // The paragraphs are the point of this channel, so the preview keeps
        // them instead of running them together.
        style={{ whiteSpace: 'pre-wrap' }}
        data-testid="preset-preview-body"
      >
        {written || PLACEHOLDER}
      </p>
    </div>
  );
}

function MessagePresetInputModal({
  originalPreset = {},
  variables = [],
  show = false,
  handleClose,
  fetchPresets,
}) {
  const [label, setLabel] = useState(originalPreset.label || '');
  const [body, setBody] = useState(originalPreset.body || '');
  // An existing row with no `channel` is a WhatsApp one — see
  // Utils/messageChannel for why that is the API's rule and not a guess.
  const [channel, setChannel] = useState(messageChannel(originalPreset.channel));
  const [subject, setSubject] = useState(originalPreset.subject || '');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  const isEdit = Boolean(originalPreset.id);
  const email = isEmailChannel(channel);
  // What would actually be stored, so the count and the emptiness test agree
  // with the server's. An email body is stored AS WRITTEN; only its ends are
  // trimmed, because the paragraphs inside it are the message.
  const wire = email ? body.trim() : oneLine(body);
  const subjectWire = oneLine(subject);
  // The cap is the WhatsApp TEMPLATE'S, so it does not apply to an email.
  const over = email ? 0 : wire.length - TEMPLATE_VARIABLE_MAX;
  const ready = Boolean(label.trim())
    && Boolean(wire)
    && over <= 0
    && (!email || Boolean(subjectWire));

  /**
   * Put a variable in at the caret.
   *
   * Clicking beats typing here: a mistyped `{pickkup}` is refused by the
   * server with a list of the right names, which is a round trip and a
   * puzzle for somebody who only wanted the pickup address.
   */
  const insert = (placeholder) => {
    const field = bodyRef.current;
    const at = Number.isInteger(field?.selectionStart) ? field.selectionStart : body.length;
    const to = Number.isInteger(field?.selectionEnd) ? field.selectionEnd : at;
    const next = body.slice(0, at) + placeholder + body.slice(to);

    // Flattened on WhatsApp only: an email body's line breaks are its own.
    setBody(email ? next : flattenLines(next));
    // Leave the caret after what was inserted, so two in a row do not
    // land on top of each other.
    window.requestAnimationFrame(() => {
      if (!field) return;
      field.focus();
      field.setSelectionRange(at + placeholder.length, at + placeholder.length);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setValidationErrors([]);
    try {
      // The STORED row always matches its channel: a WhatsApp snippet keeps
      // no subject, so switching one back and forth cannot leave a line
      // behind that no composer would ever show.
      const saved = {
        label: label.trim(),
        body: wire,
        channel,
        subject: email ? subjectWire : '',
      };
      if (isEdit) {
        await MessagePresetsService.updatePreset({ id: originalPreset.id, ...saved });
      } else {
        await MessagePresetsService.createPreset(saved);
      }
      fetchPresets();
      handleClose();
    } catch (err) {
      // The server owns the two rules a shared body can get wrong — a
      // greeting the template already supplies, and a variable it cannot
      // fill — and its wording explains both. Shown as given rather than
      // paraphrased into something vaguer.
      setError(err.response?.data?.message || 'Failed to save the saved message');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? `Edit Saved Message: ${originalPreset.label}` : 'New Saved Message'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form onSubmit={(event) => { event.preventDefault(); if (ready) handleSubmit(); }}>
          {/* FIRST, because it changes everything below it. */}
          <Form.Group className="mb-3" controlId="preset-channel">
            <Form.Label>Channel *</Form.Label>
            <Form.Select
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
              disabled={loading}
            >
              {CHANNELS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted" data-testid="preset-channel-help">
              {/* The WHY, not just the what — the one-line rule looks
                  arbitrary until you know a template variable causes it. */}
              A WhatsApp message goes inside the approved template, where the body is a
              single template variable:
              {' '}
              <strong>it must be one line</strong>
              {' '}
              — a line break fails the send, so one is flattened to spaces here — and it is
              capped at
              {' '}
              {TEMPLATE_VARIABLE_MAX}
              {' '}
              characters because WhatsApp rejects a longer one at its own edge. An
              <strong> email has no template</strong>
              , so it carries its own subject,
              {' '}
              <strong>may run to as many paragraphs as it needs</strong>
              , and has no length cap. This is why the two cannot share a snippet, and why
              a composer only offers you the ones for the channel it is on.
            </Form.Text>
            <FormControlFeedback errors={validationErrors} field="channel" />
          </Form.Group>

          <Form.Group className="mb-3" controlId="preset-label">
            <Form.Label>Button label *</Form.Label>
            <Form.Control
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Driver can't find you"
              disabled={loading}
            />
            <Form.Text className="text-muted">
              What the office sees on the button. The passenger never reads this.
            </Form.Text>
            <FormControlFeedback errors={validationErrors} field="label" />
          </Form.Group>

          {/* EMAIL ONLY. A subject is what a passenger recognises the mail
              by in a crowded inbox, so a shared one has to read sensibly on
              every booking it will ever be picked for. */}
          {email && (
            <Form.Group className="mb-3" controlId="preset-subject">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                value={subject}
                autoComplete="off"
                placeholder="e.g. Your driver is waiting"
                disabled={loading}
                onChange={(event) => setSubject(flattenLines(event.target.value))}
              />
              <Form.Text className="text-muted">
                Inserted into the composer along with the message, and editable there.
                Variables work here too.
              </Form.Text>
              <FormControlFeedback errors={validationErrors} field="subject" />
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="preset-body">
            <Form.Label>Message *</Form.Label>
            {email ? (
              <>
                <Form.Control
                  ref={bodyRef}
                  as="textarea"
                  rows={8}
                  value={body}
                  autoComplete="off"
                  placeholder={'Your driver will meet you at {pickup}.\n\n'
                    + 'Look for a sign with your name on it.'}
                  disabled={loading}
                  // NOTHING is flattened and nothing is intercepted: the
                  // paragraphs are the message on this channel.
                  onChange={(event) => setBody(event.target.value)}
                />
                <Form.Text className="text-muted">
                  As many paragraphs as it needs — no one-line rule and no length limit.
                  Start with what you want to say, not with a greeting: the composer sends
                  this as written, so a message that greets by name will greet whoever it is
                  picked for.
                </Form.Text>
              </>
            ) : (
              <>
                <Form.Control
                  ref={bodyRef}
                  type="text"
                  value={body}
                  autoComplete="off"
                  placeholder="Your driver is waiting at {pickup}"
                  disabled={loading}
                  // One line, always: the body becomes a WhatsApp template
                  // variable and a line break fails the send outright.
                  onChange={(event) => setBody(flattenLines(event.target.value))}
                  // A multi-line paste is intercepted rather than left to the
                  // browser, which STRIPS newlines and welds the words together
                  // before any change handler sees them (see Utils/oneLine).
                  onPaste={(event) => {
                    const pasted = event.clipboardData?.getData('text') || '';
                    if (!hasLineBreak(pasted)) return;
                    event.preventDefault();
                    setBody(pasteOneLine({
                      value: body,
                      pasted,
                      start: event.target.selectionStart,
                      end: event.target.selectionEnd,
                    }));
                  }}
                />
                <Form.Text className="text-muted">
                  One line. Start with what you want to say — not with a greeting.
                  <span
                    className={over > 0 ? 'text-danger fw-bold ms-1' : 'ms-1'}
                    data-testid="preset-body-count"
                  >
                    {`${wire.length}/${TEMPLATE_VARIABLE_MAX}`}
                  </span>
                </Form.Text>
                {over > 0 && (
                  <div className="text-danger small fw-bold mt-1" data-testid="preset-body-too-long">
                    {`Too long by ${over} character${over === 1 ? '' : 's'}. `}
                    Shorten it and Save will arm itself.
                  </div>
                )}
              </>
            )}
            <FormControlFeedback errors={validationErrors} field="body" />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">What the passenger reads</Form.Label>
            {email ? (
              <EmailPreview subject={subject} body={body} variables={variables} />
            ) : (
              <TemplatePreview body={body} variables={variables} />
            )}
            <Form.Text className="text-muted">
              {email
                ? 'An email carries no template: what you write is what is sent, under the '
                  + 'subject above. Variables are shown with example values; the real ones '
                  + 'come off the booking.'
                : 'The first two lines are part of the approved WhatsApp template and are '
                  + 'added for you — which is why the message must not greet anybody '
                  + 'itself. Variables are shown with example values; the real ones come '
                  + 'off the booking.'}
            </Form.Text>
          </Form.Group>

          {variables.length > 0 && (
            <div data-testid="preset-variables">
              <Form.Label className="small fw-semibold">
                Variables you can use — click one to insert it
              </Form.Label>
              <Table responsive bordered size="sm" className="mb-1">
                <tbody>
                  {variables.map((variable) => (
                    <tr key={variable.token}>
                      <td style={{ width: 190 }}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="font-monospace"
                          disabled={loading}
                          onClick={() => insert(variable.placeholder)}
                        >
                          {variable.placeholder}
                        </Button>
                      </td>
                      <td>
                        <strong className="small">{variable.label}</strong>
                        <div className="small text-muted">{variable.description}</div>
                        <div className="small text-muted">
                          Example:
                          {' '}
                          <span className="font-monospace">{variable.example}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Form.Text className="text-muted">
                A message is only offered on a booking that can fill every variable it
                uses — so one that asks for the pickup time is not offered on a booking
                that has none, rather than being sent with a gap in it.
              </Form.Text>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || !ready}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MessagePresetInputModal;
